import React, {FC, useRef} from 'react';
import styled from 'styled-components';

import {TeammateModel} from '../../../../../libs/shared/core/src/models/api/teammateModel';
import {buildTeammateDisplayName} from '../../../../../libs/shared/core/src/models/helpers/teammateModelHelpers';
import {useRequiresConnectionToggle} from '../../../../../libs/shared/core/src/services/push/pushContext';
import {getHasTeammateBusyStatusFF} from '../../../../../libs/shared/core/src/store/featureFlags/featureFlagsSelectors';
import {TeammateAvatar} from '../../../../../libs/shared/ui/src/components/avatar/teammateAvatar';
import {ButtonVariantsEnum} from '../../../../../libs/shared/ui/src/components/buttons/buttonConstants';
import {ButtonContent} from '../../../../../libs/shared/ui/src/components/buttons/buttonContent';
import {ChromelessButton} from '../../../../../libs/shared/ui/src/components/buttons/chromelessButton';
import {Icon} from '../../../../../libs/shared/ui/src/components/icon/icon';
import {VisualSizesEnum} from '../../../../../libs/shared/ui/src/styles/commonStyles';
import {boxShadow} from '../../../../../libs/shared/ui/src/theme/themeStyleHelpers';
import {useUserTheme} from '../../../../../libs/shared/ui/src/theme/userThemeProvider';
import {useAppSelector} from '../../../../helpers/appReduxHelpers';
import {AvailabilityStatusTypesEnum} from '../../../../store/data/teammates/availabilityStatusSelectors';
import {TeammateOnlineStatusesEnum} from '../../../../store/data/teammates/onlineTeammatesSelectors';
import {AvatarBadgeStatus} from '../../../common/avatars/avatarBadgeStatus';
import {DropdownPersonHeader} from '../../../common/dropdowns/dropdownPersonHeader';
import {AvailabilityDropdown} from './availabilityDropdown';
import {findTeammateAvailabilityColor, findTeammateAvailabilityIcon} from './userProfileHelpers';

/*
 * Props.
 */

interface UserProfileDropdownHeaderProps {
  teammate: TeammateModel;
  availabilityStatus: AvailabilityStatusTypesEnum;
  isConnected: boolean;
}

/*
 * Style.
 */

const StyledAvailabilityButton = styled(ChromelessButton)`
  height: 30px;
  width: 30px;
  border-radius: 50%;
  ${(p) => boxShadow(p.theme, `0 1px 3px 0 ${p.theme.alphas.black40}`)};
  display: flex;
  overflow: hidden;
  align-items: stretch;
`;

const StyledButtonContent = styled(ButtonContent)`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;
`;

const StyledDropdownPersonHeader = styled(DropdownPersonHeader)`
  padding: 12px 12px 0px 12px;
  margin-bottom: -1px;
`;

/*
 * Component.
 */

export const UserProfileDropdownHeader: FC<UserProfileDropdownHeaderProps> = ({
  teammate,
  availabilityStatus,
  isConnected,
}) => {
  const theme = useUserTheme();
  const availabilityButtonRef = useRef<HTMLDivElement>(null);
  const [isAvailabilityDropdownOpen, toggleIsAvailabilityDropdownOpen] = useRequiresConnectionToggle();
  const hasTeammateBusyStatusFF = useAppSelector(getHasTeammateBusyStatusFF);
  const buttonVariant = isConnected ? ButtonVariantsEnum.CUSTOM : ButtonVariantsEnum.PRIMARY;

  const renderAvailabilityButton = () => {
    if (hasTeammateBusyStatusFF) {
      return null;
    }
    return (
      <StyledAvailabilityButton ref={availabilityButtonRef} onClick={toggleIsAvailabilityDropdownOpen}>
        <StyledButtonContent
          isDisabled={!isConnected}
          color={findTeammateAvailabilityColor(availabilityStatus)}
          variant={buttonVariant}
          size={VisualSizesEnum.SMALL}
        >
          <Icon
            name={findTeammateAvailabilityIcon(availabilityStatus)}
            color={theme.topBar.availabilityIconColor}
          />
        </StyledButtonContent>
      </StyledAvailabilityButton>
    );
  };

  return (
    <>
      <StyledDropdownPersonHeader
        avatar={
          <TeammateAvatar teammate={teammate} size={VisualSizesEnum.LARGE}>
            {hasTeammateBusyStatusFF && (
              <AvatarBadgeStatus
                teammateStatus={teammate.availabilityStatus}
                availabilityStatus={availabilityStatus}
                size={VisualSizesEnum.MEDIUM}
                onlineStatus={
                  isConnected ? TeammateOnlineStatusesEnum.ONLINE : TeammateOnlineStatusesEnum.OFFLINE
                }
                borderColor={theme.foreground}
              />
            )}
          </TeammateAvatar>
        }
        title={buildTeammateDisplayName(teammate)}
        description={`@${teammate.alias}`}
        accessory={renderAvailabilityButton()}
      />
      {isAvailabilityDropdownOpen && (
        <AvailabilityDropdown
          anchor={availabilityButtonRef}
          onRequestClose={toggleIsAvailabilityDropdownOpen}
          teammate={teammate}
        />
      )}
    </>
  );
};
